import { useCallback, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useTitle } from "../useTitle";
import { Board, Loading, Notice, PageHead, Views } from "../components/Look";
import {
  ApiError,
  ROLES,
  api,
  ago,
  roleMeans,
  roleName,
  type Group,
  type GroupMember,
  type Role,
  type User,
} from "../api";

/**
 * People and groups: the accounts that exist and the teams they are in.
 *
 * WHAT IS NOT HERE, DELIBERATELY: granting anybody anything. A role is granted
 * on a project or on a bench, by whoever owns that project or bench, on that
 * thing's own page. If access could also be handed out from here, there would
 * be two places to look when it has to be taken away, and only one of them
 * would be looked at.
 *
 * READABLE BY EVERYONE, EDITABLE BY AN ADMINISTRATOR. A project owner has to be
 * able to see who exists in order to grant them anything, so the list is not an
 * admin page; what a non-admin does not get is the rest of the record, and the
 * buttons.
 */
export default function People() {
  const { t } = useTranslation();
  useTitle(t("people.title"));
  const [tab, setTab] = useState<"users" | "groups">("users");
  const [admin, setAdmin] = useState(false);
  // How many people and teams there are: the page's own question, asked the
  // way every other page asks its own. Quiet for a reader who may not list them.
  const [counts, setCounts] = useState<{ users: number; groups: number } | null>(null);
  useEffect(() => {
    let alive = true;
    Promise.all([api.users(), api.groups()])
      .then(([u, g]) => {
        if (alive) setCounts({ users: (u.users ?? []).length, groups: (g.groups ?? []).length });
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  return (
    <section>
      <PageHead
        title={t("people.heading")}
        answer={
          counts
            ? `${t("people.accountCount", { count: counts.users })}, ${t("people.groupCount", { count: counts.groups })}.`
            : undefined
        }
        after={<p className="text-sm text-slate-500">{t("people.intro")}</p>}
      />
      {/* The counts ARE the page's answer, so their absence is what "still
          coming" means here; no second flag for the same fact. */}
      {!counts && <Loading label={t("common.loading")} />}
      <div className="mb-4">
        <Views
          label={t("people.heading")}
          value={tab}
          onChange={setTab}
          items={[["users", t("people.tabAccounts")], ["groups", t("people.tabGroups")]]}
        />
      </div>

      {tab === "users" ? (
        <Users onAdmin={setAdmin} />
      ) : (
        <Groups admin={admin} />
      )}
    </section>
  );
}

function Err({ children }: { children: React.ReactNode }) {
  return <Notice tone="error">{children}</Notice>;
}

/** A password the server generated, shown once because it is stored hashed. */
function Once({ pw, who }: { pw: string; who: string }) {
  const { t } = useTranslation();
  return (
    <div className="my-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100">
      <Trans
        i18nKey="people.passwordFor"
        values={{ who, pw }}
        components={{ b: <strong />, code: <code className="font-mono" /> }}
      />
      <div className="mt-1 text-xs">{t("people.passwordOnce")}</div>
    </div>
  );
}

function Users({ onAdmin }: { onAdmin: (v: boolean) => void }) {
  const { t } = useTranslation();
  const [users, setUsers] = useState<User[]>([]);
  const [admin, setAdmin] = useState(false);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [display, setDisplay] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [shown, setShown] = useState<{ who: string; pw: string } | null>(null);
  const [open, setOpen] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      const d = await api.users();
      setUsers(d.users);
      setAdmin(d.admin);
      onAdmin(d.admin);
      setError("");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  }, [onAdmin]);

  useEffect(() => {
    void load();
  }, [load]);

  async function act(fn: () => Promise<unknown>) {
    setError("");
    try {
      await fn();
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  }

  return (
    <>
      {admin && (
        <div className="mb-3">
          <button
            className="btn-primary"
            onClick={() => setCreating(!creating)}
          >
            {t("people.newAccount")}
          </button>
        </div>
      )}

      {error && <Err>{error}</Err>}
      {shown && <Once who={shown.who} pw={shown.pw} />}

      {creating && (
        <form
          className="mb-4 flex flex-wrap items-end gap-2 rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900"
          onSubmit={async (e) => {
            e.preventDefault();
            setError("");
            try {
              const r = await api.createUser({
                name: name.trim(),
                display_name: display.trim(),
                is_admin: isAdmin,
              });
              if (r.password) setShown({ who: name.trim(), pw: r.password });
              setName("");
              setDisplay("");
              setIsAdmin(false);
              setCreating(false);
              await load();
            } catch (err) {
              setError(err instanceof ApiError ? err.message : String(err));
            }
          }}
        >
          <label className="text-sm">
            <span className="mb-1 block text-slate-600 dark:text-slate-400">
              {t("people.username")}
            </span>
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("people.usernamePlaceholder")}
              className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-950"
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-slate-600 dark:text-slate-400">
              {t("people.fullName")}
            </span>
            <input
              value={display}
              onChange={(e) => setDisplay(e.target.value)}
              placeholder={t("people.fullNamePlaceholder")}
              className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-950"
            />
          </label>
          <label className="flex items-center gap-2 py-2 text-sm">
            <input
              type="checkbox"
              checked={isAdmin}
              onChange={(e) => setIsAdmin(e.target.checked)}
            />
            {t("people.instanceAdmin")}
          </label>
          <button
            disabled={!name.trim()}
            className="btn-primary"
          >
            {t("people.create")}
          </button>
          <button type="button" className="px-2 py-2 text-sm" onClick={() => setCreating(false)}>
            {t("common.cancel")}
          </button>
          <p className="w-full text-xs text-slate-500">{t("people.createNote")}</p>
        </form>
      )}

      <Board as="ul">
        {users.map((u) => (
          <li key={u.id} className="px-3 py-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium">{u.name}</span>
              {u.display_name && (
                <span className="text-sm text-slate-500">{u.display_name}</span>
              )}
              {u.is_admin && (
                <span className="rounded bg-brand-700 px-1.5 py-0.5 text-xs font-semibold text-white">
                  {t("people.badgeAdmin")}
                </span>
              )}
              {u.source === "sso" && (
                <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                  {t("people.badgeSso")}
                </span>
              )}
              {!u.is_active && (
                <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-900 dark:bg-amber-900 dark:text-amber-100">
                  {t("people.badgeDeactivated")}
                </span>
              )}
              {admin && (
                <span className="ml-auto flex flex-wrap gap-2 text-sm">
                  <button
                    className="text-slate-600 hover:underline dark:text-slate-300"
                    onClick={() => setOpen(open === u.id ? null : u.id)}
                  >
                    {open === u.id ? t("people.hide") : t("people.reach")}
                  </button>
                  <button
                    className="text-slate-600 hover:underline dark:text-slate-300"
                    onClick={() =>
                      void act(async () => {
                        const r = await api.resetPassword(u.id);
                        if (r.password) setShown({ who: u.name, pw: r.password });
                      })
                    }
                  >
                    {t("people.resetPassword")}
                  </button>
                  <button
                    className="text-slate-600 hover:underline dark:text-slate-300"
                    onClick={() =>
                      void act(() => api.updateUser(u.id, { is_admin: !u.is_admin }))
                    }
                  >
                    {u.is_admin ? t("people.revokeAdmin") : t("people.makeAdmin")}
                  </button>
                  <button
                    className="text-red-700 hover:underline dark:text-red-400"
                    onClick={() =>
                      void act(() => api.updateUser(u.id, { is_active: !u.is_active }))
                    }
                  >
                    {u.is_active ? t("people.deactivate") : t("people.reactivate")}
                  </button>
                </span>
              )}
            </div>
            {admin && (
              <p className="mt-0.5 text-xs text-slate-500">
                {t("people.lastSignedIn", { when: ago(u.last_login_at) })}
                {u.group_count
                  ? t("people.inGroups", { count: u.group_count })
                  : ""}
                {u.must_change_password ? t("people.mustChange") : ""}
              </p>
            )}
            {open === u.id && <Reach id={u.id} />}
          </li>
        ))}
      </Board>
      {users.length === 0 && !error && (
        <p className="mt-2 text-sm text-slate-500">
          {t("people.noAccounts")}
        </p>
      )}
    </>
  );
}

/**
 * Every grant that reaches one person, and what it comes through.
 *
 * The question this answers is "why can they take that bench" - or, far more
 * often, "why can they NOT" - and before it existed the answer was a query
 * somebody had to write against three tables.
 */
function Reach({ id }: { id: number }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<
    { role: Role; scope: string; scope_id: number; scope_name: string; through: string }[]
  >([]);
  const [error, setError] = useState("");

  useEffect(() => {
    void (async () => {
      try {
        setRows((await api.user(id)).grants);
      } catch (e) {
        setError(e instanceof ApiError ? e.message : String(e));
      }
    })();
  }, [id]);

  if (error) return <Err>{error}</Err>;
  if (rows.length === 0)
    return (
      <p className="mt-2 text-xs text-slate-500">
        {t("people.noGrants")}
      </p>
    );
  return (
    <ul className="mt-2 space-y-1 text-xs text-slate-600 dark:text-slate-300">
      {rows.map((g, i) => (
        <li key={i}>
          <Trans
            i18nKey="people.grantOn"
            values={{ role: roleName(g.role), scope: g.scope_name }}
            components={{
              b: <span className="font-medium" />,
              scopeLink: (
                <Link
                  className="underline"
                  to={`/${g.scope === "project" ? "projects" : "benches"}/${g.scope_id}`}
                />
              ),
            }}
          />
          {g.through
            ? t("people.throughGroup", { group: g.through })
            : t("people.grantedDirectly")}
        </li>
      ))}
    </ul>
  );
}

function Groups({ admin }: { admin: boolean }) {
  const { t } = useTranslation();
  const [groups, setGroups] = useState<Group[]>([]);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      setGroups((await api.groups()).groups);
      setError("");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <>
      {admin && (
        <div className="mb-3">
          <button
            className="btn-primary"
            onClick={() => setCreating(!creating)}
          >
            {t("people.newGroup")}
          </button>
        </div>
      )}
      {error && <Err>{error}</Err>}

      {creating && (
        <form
          className="mb-4 flex flex-wrap gap-2 rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900"
          onSubmit={async (e) => {
            e.preventDefault();
            try {
              await api.createGroup(name.trim(), "");
              setName("");
              setCreating(false);
              await load();
            } catch (err) {
              setError(err instanceof ApiError ? err.message : String(err));
            }
          }}
        >
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("people.groupNamePlaceholder")}
            className="flex-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-950"
          />
          <button className="btn-primary">
            {t("people.create")}
          </button>
          <button type="button" className="px-3 text-sm" onClick={() => setCreating(false)}>
            {t("common.cancel")}
          </button>
        </form>
      )}

      {groups.length === 0 && !error && (
        <p className="text-sm text-slate-500">
          {t("people.noGroups")}
        </p>
      )}

      <Board as="ul">
        {groups.map((g) => (
          <li key={g.id} className="px-3 py-2">
            <div className="flex flex-wrap items-center gap-2">
              <button
                className="font-medium hover:underline"
                onClick={() => setOpen(open === g.id ? null : g.id)}
              >
                {g.name}
              </button>
              <span className="text-xs text-slate-500">
                {t("people.memberCount", { count: g.member_count })} ·{" "}
                {t("people.grantCount", { count: g.grant_count })}
              </span>
              {g.description && (
                <span className="text-sm text-slate-500">{g.description}</span>
              )}
            </div>
            {open === g.id && <GroupPanel id={g.id} onChanged={load} />}
          </li>
        ))}
      </Board>
    </>
  );
}

/** One group opened: its people, and where it has been granted a role. */
function GroupPanel({ id, onChanged }: { id: number; onChanged: () => void }) {
  const { t } = useTranslation();
  const [members, setMembers] = useState<GroupMember[]>([]);
  const [grants, setGrants] = useState<
    { id: number; role: Role; scope: string; scope_id: number; scope_name: string }[]
  >([]);
  const [mayEdit, setMayEdit] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [pick, setPick] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const d = await api.group(id);
      setMembers(d.members);
      setGrants(d.grants);
      setMayEdit(d.may_edit);
      setUsers((await api.users()).users);
      setError("");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function act(fn: () => Promise<unknown>) {
    setError("");
    try {
      await fn();
      await load();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  }

  const inGroup = new Set(members.map((m) => m.user_id));

  return (
    <div className="mt-2 rounded-md bg-slate-50 p-3 dark:bg-slate-950">
      {error && <Err>{error}</Err>}
      <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
        {t("people.groupPeople")}
      </h4>
      <ul className="mt-1 space-y-1 text-sm">
        {members.map((m) => (
          <li key={m.user_id} className="flex items-center gap-2">
            <span>{m.name}</span>
            {m.manager ? (
              <span className="rounded bg-slate-200 px-1.5 py-0.5 text-xs dark:bg-slate-800">
                {t("people.manager")}
              </span>
            ) : null}
            {mayEdit && (
              <button
                className="ml-auto text-red-700 hover:underline dark:text-red-400"
                onClick={() => void act(() => api.removeFromGroup(id, m.user_id))}
              >
                {t("people.remove")}
              </button>
            )}
          </li>
        ))}
        {members.length === 0 && (
          <li className="text-slate-500">{t("people.groupEmpty")}</li>
        )}
      </ul>

      {mayEdit && (
        <form
          className="mt-2 flex flex-wrap gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (pick) void act(() => api.addToGroup(id, Number(pick)));
            setPick("");
          }}
        >
          <select
            aria-label={t("people.addToGroup")}
            className="rounded-md border border-slate-300 bg-white px-2 py-1 text-sm dark:border-slate-700 dark:bg-slate-950"
            value={pick}
            onChange={(e) => setPick(e.target.value)}
          >
            <option value="">{t("people.addSomebody")}</option>
            {users
              .filter((u) => !inGroup.has(u.id))
              .map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
          </select>
          <button
            disabled={!pick}
            className="rounded-md border border-slate-300 px-2 py-1 text-sm disabled:opacity-40 dark:border-slate-700"
          >
            {t("people.add")}
          </button>
        </form>
      )}

      <h4 className="mt-3 text-sm font-semibold text-slate-700 dark:text-slate-300">
        {t("people.whereRole")}
      </h4>
      {grants.length === 0 ? (
        <p className="mt-1 text-sm text-slate-500">
          {t("people.noWhere")}
        </p>
      ) : (
        <ul className="mt-1 space-y-1 text-sm">
          {grants.map((g) => (
            <li key={g.id}>
              <Trans
                i18nKey="people.grantOn"
                values={{ role: roleName(g.role), scope: g.scope_name }}
                components={{
                  b: <span className="font-medium" />,
                  scopeLink: (
                    <Link
                      className="underline"
                      to={`/${g.scope === "project" ? "projects" : "benches"}/${g.scope_id}`}
                    />
                  ),
                }}
              />
              <span className="text-xs text-slate-500"> — {roleMeans(g.role)}</span>
            </li>
          ))}
        </ul>
      )}
      {/* THE LADDER, BEHIND A CLICK, as it already is on a project and on a
          bench (members.roleKeySummary). It was printed open here, on the one
          page that grants nothing, so every visitor read the whole glossary
          before reaching the accounts they came for. */}
      <details className="mt-2">
        <summary className="cursor-pointer text-xs text-slate-500">
          {t("members.roleKeySummary")}
        </summary>
        <p className="mt-1 text-xs text-slate-500">
          {t("people.groupNote", {
            what: grants.length ? t("people.whatAbove") : t("people.whatWhatever"),
            ladder: ROLES.map(roleName).join(" < "),
          })}
        </p>
      </details>
    </div>
  );
}
